export type SkillCategory = string;

export type SkillDef = {
  id: string;
  name: string;
  category: SkillCategory;
  icon: string;
  description: string;
  body: string;
  version: string;
  latency: string;
  inputs: { name: string; type: string }[];
  outputs: { name: string; type: string }[];
  discipline?: string;
};

export const CATEGORY_COLORS: Record<string, string> = {
  Review: '#4AD5CA',
  Test: '#00B894',
  Security: '#E17055',
  Generate: '#FDCB6E',
  Other: '#95A5A6',
};

// categoryColor returns a color for any category string, hashing unknown ones.
export function categoryColor(cat: string): string {
  if (CATEGORY_COLORS[cat]) return CATEGORY_COLORS[cat];
  let h = 0;
  for (let i = 0; i < cat.length; i++) h = (h * 31 + cat.charCodeAt(i)) >>> 0;
  const palette = ['#4AD5CA', '#00B894', '#E17055', '#FDCB6E', '#0984E3', '#00CEC9', '#FD79A8', '#A29BFE'];
  return palette[h % palette.length];
}

export const SKILLS: SkillDef[] = [
  {
    id: 'code-review',
    name: 'Code Review',
    category: 'Review',
    icon: 'ri-search-eye-line',
    description: 'Reviews diff for style, bugs, and clarity.',
    body: 'You are a senior code reviewer.\nAnalyze the provided diff for bugs, style issues, and readability problems.\nReturn a list of findings, each with severity, file path, and a suggested fix.',
    version: 'v1.4',
    latency: 'Est. 2.3s',
    inputs: [
      { name: 'diff', type: 'string' },
      { name: 'files', type: 'array<string>' },
    ],
    outputs: [
      { name: 'review_findings', type: 'array<finding>' },
      { name: 'severity_max', type: 'string' },
    ],
  },
  {
    id: 'lint-fix',
    name: 'Lint & Fix',
    category: 'Review',
    icon: 'ri-magic-line',
    description: 'Runs linter, applies auto-fixes.',
    body: 'Run the project linter on the given files.\nApply safe auto-fixes and report any issues that require manual attention.',
    version: 'v0.9',
    latency: 'Est. 1.1s',
    inputs: [{ name: 'files', type: 'array<string>' }],
    outputs: [{ name: 'lint_report', type: 'object' }],
  },
  {
    id: 'test-gen',
    name: 'Test Generation',
    category: 'Test',
    icon: 'ri-test-tube-line',
    description: 'Generates unit tests from changed functions.',
    body: 'Generate unit tests for the changed functions.\nCover happy paths and key edge cases.\nOutput tests as runnable code plus a coverage delta estimate.',
    version: 'v2.0',
    latency: 'Est. 4.2s',
    inputs: [{ name: 'files', type: 'array<string>' }],
    outputs: [
      { name: 'tests', type: 'array<test>' },
      { name: 'coverage_delta', type: 'number' },
    ],
  },
  {
    id: 'perf-audit',
    name: 'Performance Audit',
    category: 'Test',
    icon: 'ri-speed-up-line',
    description: 'Detects perf regressions in hot paths.',
    body: 'Audit the diff for performance regressions in hot paths.\nFlag N+1 queries, unnecessary allocations, and blocking calls.',
    version: 'v1.2',
    latency: 'Est. 3.8s',
    inputs: [{ name: 'diff', type: 'string' }],
    outputs: [{ name: 'perf_report', type: 'object' }],
  },
  {
    id: 'security-scan',
    name: 'Security Scan',
    category: 'Security',
    icon: 'ri-shield-keyhole-line',
    description: 'SAST scan for common vulnerabilities.',
    body: 'Run a static security scan over the diff.\nLook for injection, XSS, secrets leakage, and unsafe deserialization.\nReturn findings grouped by severity.',
    version: 'v3.1',
    latency: 'Est. 5.4s',
    inputs: [
      { name: 'diff', type: 'string' },
      { name: 'files', type: 'array<string>' },
    ],
    outputs: [
      { name: 'security_findings', type: 'array<finding>' },
      { name: 'severity_max', type: 'string' },
    ],
  },
  {
    id: 'dep-check',
    name: 'Dependency Check',
    category: 'Security',
    icon: 'ri-git-branch-line',
    description: 'Checks for vulnerable dependencies.',
    body: 'Check the project dependencies for known vulnerabilities.\nReport any package that has a published CVE with the suggested upgrade path.',
    version: 'v1.0',
    latency: 'Est. 1.9s',
    inputs: [{ name: 'files', type: 'array<string>' }],
    outputs: [{ name: 'dep_report', type: 'object' }],
  },
  {
    id: 'doc-gen',
    name: 'Doc Generation',
    category: 'Generate',
    icon: 'ri-file-text-line',
    description: 'Writes docstrings for changed functions.',
    body: 'Write clear docstrings for every changed public function.\nFollow the project style and include params, return type, and examples.',
    version: 'v1.5',
    latency: 'Est. 2.7s',
    inputs: [{ name: 'files', type: 'array<string>' }],
    outputs: [{ name: 'docs', type: 'string' }],
  },
  {
    id: 'pr-summary',
    name: 'PR Summary',
    category: 'Generate',
    icon: 'ri-quill-pen-line',
    description: 'Summarizes changes into a PR description.',
    body: 'Summarize the collected findings into a concise PR description.\nInclude sections for changes, tests, and remaining risks.\nOutput as markdown.',
    version: 'v2.2',
    latency: 'Est. 1.6s',
    inputs: [
      { name: 'review_findings', type: 'array<finding>' },
      { name: 'security_findings', type: 'array<finding>' },
      { name: 'tests', type: 'array<test>' },
    ],
    outputs: [{ name: 'summary_md', type: 'string' }],
  },
];

export type NodeStatus = 'idle' | 'running' | 'success' | 'warning' | 'error';

export type PipelineNode = {
  id: string;
  skillId: string;
  x: number;
  y: number;
  status: NodeStatus;
  note?: string;
  body?: string;
};

export type GroupType = 'process' | 'loop';

export type GroupNode = {
  id: string;
  type: GroupType;
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  status: NodeStatus;
  iterations: number;
};

export type StickyNote = {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  text: string;
};

export type PollAction = { kind?: string; url?: string; query?: string; urls?: string[] };

export type Scheduler = {
  id: string;
  x: number;
  y: number;
  title?: string;
  enabled: boolean;
  cadence?: string;
  timezone?: string;
  action?: PollAction;
  targetIds?: string[];
  lastFiredAt?: string;
};

export type PipelineEdge = {
  id: string;
  from: string; // node id
  to: string;
  field: string;
};

export const INITIAL_NODES: PipelineNode[] = [
  { id: 'n1', skillId: 'code-review', x: 80, y: 120, status: 'success' },
  { id: 'n2', skillId: 'test-gen', x: 380, y: 60, status: 'success' },
  { id: 'n3', skillId: 'security-scan', x: 380, y: 240, status: 'warning' },
  { id: 'n4', skillId: 'pr-summary', x: 700, y: 150, status: 'success' },
];

export const INITIAL_EDGES: PipelineEdge[] = [
  { id: 'e1', from: 'n1', to: 'n2', field: 'files' },
  { id: 'e2', from: 'n1', to: 'n3', field: 'diff' },
  { id: 'e3', from: 'n2', to: 'n4', field: 'tests' },
  { id: 'e4', from: 'n3', to: 'n4', field: 'security_findings' },
];

export const TEST_RESULTS = [
  { name: 'Workflow sequence order', passed: true },
  { name: 'Expected output shape', passed: true },
  { name: 'Handles multi-file diffs', passed: true },
  { name: 'Edge case: empty file diff', passed: false, reason: 'PR Summary crashed on empty tests array' },
];

export const SUGGESTIONS = [
  'Add handling for empty files in step 3',
  'Specify output format with an example in the skill body',
  'Consider adding Lint & Fix before Code Review to reduce noise',
];

export const OUTCOME_MD = `# pr-quality-gate v2.1

**Score:** 8.4 / 12
**Steps:** 4
**Agent:** Claude Code

## Pipeline
1. Code Review → files, review_findings
2. Test Generation → tests
3. Security Scan → security_findings
4. PR Summary → summary_md

## Test Results
- ✓ Workflow sequence order
- ✓ Expected output shape
- ✓ Handles multi-file diffs
- ✗ Edge case: empty file diff — PR Summary crashed on empty tests array

## Suggestions
- Add handling for empty files in step 3
- Specify output format with an example in the skill body
`;